/**
 * SEO Component
 * Reusable head tag manager using react-helmet-async.
 * Provides per-page title, meta description, Open Graph tags,
 * canonical URLs, and JSON-LD structured data.
 */

import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'MemAds';
const DEFAULT_DESCRIPTION =
  "India's modern classifieds marketplace. Buy and sell cars, mobiles, furniture, electronics, and more with verified sellers and secure local chat.";
const SITE_URL = typeof window !== 'undefined' ? window.location.origin : 'https://memads.in';

interface SEOProps {
  /** Page-specific title. Auto-appends " | MemAds" unless `titleTemplate` is false. */
  title?: string;
  /** Meta description for the page. Falls back to site default. */
  description?: string;
  /** Canonical URL path (e.g. "/listings"). Full URL is built automatically. */
  canonical?: string;
  /** Open Graph image URL. Falls back to /og-default.png. */
  ogImage?: string;
  /** Open Graph type. Defaults to "website". */
  ogType?: 'website' | 'article' | 'product';
  /** If true, adds noindex/nofollow meta. Use for login, admin, profile, etc. */
  noindex?: boolean;
  /** JSON-LD structured data object. Rendered as <script type="application/ld+json">. */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** If true, use the title exactly as-is without appending " | MemAds". */
  rawTitle?: boolean;
}

const SEO: React.FC<SEOProps> = ({
  title,
  description = DEFAULT_DESCRIPTION,
  canonical,
  ogImage,
  ogType = 'website',
  noindex = false,
  jsonLd,
  rawTitle = false,
}) => {
  const fullTitle = title
    ? rawTitle
      ? title
      : `${title} | ${SITE_NAME}`
    : `${SITE_NAME} — India's Premium Local Marketplace`;

  const canonicalUrl = canonical
    ? `${SITE_URL}${canonical.startsWith('/') ? canonical : `/${canonical}`}`
    : undefined;

  const ogImageUrl = ogImage || `${SITE_URL}/og-default.png`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />

      {/* Canonical */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* Robots */}
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:site_name" content={SITE_NAME} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />

      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
